import { useState, useCallback } from "react";
import {
  postLotAnalysis,
  getLotFilesData,
  getLotAnalysisSummary,
} from "../api/lots";

const ERROR_MAP = {
  "Technical specification has no summary": "Техническое задание не содержит данных для анализа",
  "Failed to extract summary": "Не удалось извлечь данные из технического задания",
  "Unsupported file format": "Формат файла не поддерживается",
  "Empty technical specification": "Техническое задание пустое",
  "No description found": "Описание лота отсутствует",
};

export const useLotAnalysis = () => {
  const [isOpen, setIsOpen] = useState(false);

  const [loading, setLoading] = useState(false);
  const [waitingResult, setWaitingResult] = useState(false); // режим ожидания "Анализ выполняется…"


  const [fileData, setFileData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);


  const translateError = (msg) => {
  if (!msg) return "Неизвестная ошибка";

  return ERROR_MAP[msg] || `Ошибка: ${msg}`;
};

  // Универсальная загрузка данных (2 GET)
  const loadResults = useCallback(async (lotId, id) => {
  // Запускаем оба GET независимо
  const [filesRes, summaryRes] = await Promise.allSettled([
    getLotFilesData(lotId),
    getLotAnalysisSummary(id),
  ]);

  let hasProgress = false;

  // === Обработка files ===
  if (filesRes.status === "fulfilled") {
    setFileData(filesRes.value?.data || {});
  } else {
    const err = filesRes.reason;

    if (err.code === "AnalysisInProgress") {
      hasProgress = true;
    } else {
      setError(translateError(err.message));
    }
  }

  // === Обработка summary ===
  if (summaryRes.status === "fulfilled") {
    setSummary(summaryRes.value || {});
  } else {
    const err = summaryRes.reason;

    if (err.code === "AnalysisInProgress") {
      hasProgress = true;
    } else {
      setError(translateError(err.message));
    }
  }

  // Если хоть один GET сказал "анализ ещё идёт"
  if (hasProgress) {
    setWaitingResult(true);
  }
}, []);

  // Первый запуск анализа
  const analyzeLot = useCallback(
    async (lot) => {
      const { id, lotId, analyzedByUser } = lot;

      setIsOpen(true);
      setError(null);
      setWaitingResult(false);
      setFileData(null);
      setSummary(null);
      // ------ A. Уже анализировался → сразу GET ------
      if (analyzedByUser) {
        setLoading(true);
        try {
          await loadResults(lotId, id);
        } catch (err) {
          // GET → AnalysisInProgress
          if (err.code === "AnalysisInProgress") {
            setWaitingResult(true);
              setLoading(false);   // ← ОБЯЗАТЕЛЬНО

            return;
          }
          setError(translateError(err.message));
        } finally {
          setLoading(false);
        }
        return;
      }
      // ------ B. Первый запуск анализа: POST ------
      setLoading(true);
      try {
        try {
          await postLotAnalysis(id);
          // POST 200 → статус Pending → нужно ждать результат
        } catch (err) {
          // POST → AnalysisInProgress
          if (err.code === "AnalysisInProgress") {
            setWaitingResult(true);
              setLoading(false);   // ← ОБЯЗАТЕЛЬНО
            return;
          }
          // POST → "You've already analyzed this lot"
          if (err.message === "You've already analyzed this lot") {
            // Актуализируем состояние → сразу GET
            try {
              await loadResults(lotId, id);
            } catch (e) {
              if (e.code === "AnalysisInProgress") {
                setWaitingResult(true);
                  setLoading(false);   // ← ОБЯЗАТЕЛЬНО
                return;
              }
              setError(translateError(e.message));
            } finally {
              setLoading(false);
            }
            return;
          }

          throw err;
        }

        // Если POST был успешным → GET результат
        try {
          await loadResults(lotId, id);
        } catch (err) {
          if (err.code === "AnalysisInProgress") {
            setWaitingResult(true);
              setLoading(false);   // ← ОБЯЗАТЕЛЬНО

            return;
          }
          setError(translateError(err.message));
        }
      } catch (err) {
        setError(translateError(err.message));
      } finally {
        setLoading(false);
      }
    },
    [loadResults]
  );

  // Кнопка "Обновить"
  const retryAnalysis = useCallback(
    async (lot) => {
      if (!lot) return;

      const { id, lotId } = lot;

      setLoading(true);
      setError(null);

      try {
        // В режиме ожидания вызываем ТОЛЬКО GET
        try {
          await loadResults(lotId, id);
          setWaitingResult(false);
          return;
        } catch (err) {
          if (err.code === "AnalysisInProgress") {
            setWaitingResult(true);
              setLoading(false);   // ← ОБЯЗАТЕЛЬНО

            return;
          }
          throw err;
        }
      } catch (err) {
        setError(translateError(err.message));
      } finally {
        setLoading(false);
      }
    },
    [loadResults]
  );

  const closeModal = useCallback(() => {
    setIsOpen(false);
    setLoading(false);
    setWaitingResult(false);
    setError(null);
    setFileData(null);
    setSummary(null);
  }, []);

  return {
    isOpen,
    loading,
    waitingResult,
    error,
    fileData,
    summary,
    analyzeLot,
    retryAnalysis,
    closeModal,
  };
};
